import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useMetricsData } from '../../lib/admin-hooks';
import { useMetricsDateRange, generateMetricsCsv, downloadCsv } from '../../lib/metrics-hooks';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatCurrency } from '../../lib/utils';
import { MetricsDateFilter } from '../../components/admin/MetricsDateFilter';
import { MetricsPeakInsights } from '../../components/admin/MetricsPeakInsights';
import { DailySalesChart, HourlySalesChart, ChannelPieChart, TopProductsChart } from '../../components/admin/MetricsCharts';
import { DownloadIcon, TrendingUp, TrendingDown, Loader2Icon } from 'lucide-react';
function getDefaultDateRange(): {
  start: string;
  end: string;
} {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return {
    start: fmt(firstDay),
    end: fmt(today)
  };
}
export function MetricsPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const {
    profitability,
    loading: metricsLoading
  } = useMetricsData(restaurant?.id);
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  const defaultRange = getDefaultDateRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const {
    orders,
    analytics,
    loading: analyticsLoading
  } = useMetricsDateRange(restaurant?.id, startDate, endDate);
  const [isExporting, setIsExporting] = useState(false);
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-2983">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-2984" />
      </div>;
  }
  const handleDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };
  const handleExportCsv = () => {
    setIsExporting(true);
    try {
      const csv = generateMetricsCsv(analytics, orders);
      const filename = `metricas_${restaurant.slug}_${startDate}_${endDate}.csv`;
      downloadCsv(csv, filename);
    } catch (err) {
      console.error('Error exporting CSV:', err);
    } finally {
      setTimeout(() => setIsExporting(false), 600);
    }
  };
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-2985">
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6" data-id="element-2986">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4" data-id="element-2987">
          <div data-id="element-2988">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-2989">
              Métricas y Análisis
            </h1>
            <p className="text-gray-500 text-sm mt-1" data-id="element-2990">
              Análisis detallado de ventas, tendencias y rendimiento
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={handleExportCsv} disabled={analyticsLoading || orders.length === 0} isLoading={isExporting} className="flex-shrink-0" data-id="element-2991">
            <DownloadIcon className="w-4 h-4 mr-2" data-id="element-2992" />
            Exportar CSV
          </Button>
        </div>

        {/* Date Filter */}
        <Card data-id="element-2993">
          <CardContent className="p-4" data-id="element-2994">
            <MetricsDateFilter startDate={startDate} endDate={endDate} onChange={handleDateChange} data-id="element-2995" />
          </CardContent>
        </Card>

        {/* Loading State */}
        {analyticsLoading ? <div className="space-y-4" data-id="element-2996">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-2997">
              <Skeleton className="h-28 rounded-xl" data-id="element-2998" />
              <Skeleton className="h-28 rounded-xl" data-id="element-2999" />
              <Skeleton className="h-28 rounded-xl" data-id="element-3000" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3001">
              <Skeleton className="h-36 rounded-xl" data-id="element-3002" />
              <Skeleton className="h-36 rounded-xl" data-id="element-3003" />
              <Skeleton className="h-36 rounded-xl" data-id="element-3004" />
              <Skeleton className="h-36 rounded-xl" data-id="element-3005" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-3006">
              <Skeleton className="h-80 rounded-xl" data-id="element-3007" />
              <Skeleton className="h-80 rounded-xl" data-id="element-3008" />
            </div>
          </div> : <>
            {/* Peak Insights & KPIs */}
            <MetricsPeakInsights peakDays={analytics.peakDays} peakHours={analytics.peakHours} channelBreakdown={analytics.channelBreakdown} totalOrders={analytics.totalOrders} totalRevenue={analytics.totalRevenue} averageTicket={analytics.averageTicket} data-id="element-3009" />

            {/* Charts Row 1: Daily Sales + Hourly Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-3010">
              <DailySalesChart data={analytics.dailySales} peakDays={analytics.peakDays} data-id="element-3011" />
              <HourlySalesChart data={analytics.hourlySales} peakHours={analytics.peakHours} data-id="element-3012" />
            </div>

            {/* Charts Row 2: Channel Pie + Top Products */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-3013">
              <ChannelPieChart data={analytics.channelBreakdown} data-id="element-3014" />
              <TopProductsChart data={analytics.topProducts} data-id="element-3015" />
            </div>
          </>}

        {/* Profitability Table */}
        <Card data-id="element-3016">
          <CardHeader data-id="element-3017">
            <CardTitle data-id="element-3018">Rentabilidad por Producto</CardTitle>
          </CardHeader>
          <CardContent data-id="element-3019">
            {metricsLoading ? <div className="space-y-4" data-id="element-3020">
                <Skeleton className="h-12 w-full" data-id="element-3021" />
                <Skeleton className="h-12 w-full" data-id="element-3022" />
                <Skeleton className="h-12 w-full" data-id="element-3023" />
                <Skeleton className="h-12 w-full" data-id="element-3024" />
                <Skeleton className="h-12 w-full" data-id="element-3025" />
              </div> : profitability.length > 0 ? <div className="overflow-x-auto -mx-6" data-id="element-3026">
                <table className="w-full text-sm text-left min-w-[600px]" data-id="element-3027">
                  <thead className="text-xs text-gray-500 uppercase bg-gray-50" data-id="element-3028">
                    <tr data-id="element-3029">
                      <th className="px-6 py-3 rounded-l-lg" data-id="element-3030">Producto</th>
                      <th className="px-4 py-3" data-id="element-3031">Categoría</th>
                      <th className="px-4 py-3 text-right" data-id="element-3032">Precio Venta</th>
                      <th className="px-4 py-3 text-right" data-id="element-3033">Costo Est.</th>
                      <th className="px-4 py-3 text-right" data-id="element-3034">Ganancia</th>
                      <th className="px-4 py-3 text-right rounded-r-lg" data-id="element-3035">
                        Margen
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-3036">
                    {profitability.map(item => {
                  const isGoodMargin = item.margin_percent >= 60;
                  const isBadMargin = item.margin_percent < 30;
                  return <tr key={item.menu_item_id} className="border-b border-gray-50 last:border-0" data-id="element-3037">
                          <td className="px-6 py-3 font-medium text-gray-900" data-id="element-3038">
                            {item.item_name}
                          </td>
                          <td className="px-4 py-3 text-gray-500" data-id="element-3039">
                            {item.category || '—'}
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-3040">
                            {formatCurrency(item.price)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500" data-id="element-3041">
                            {formatCurrency(item.estimated_cost)}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-emerald-600" data-id="element-3042">
                            {formatCurrency(item.estimated_margin)}
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-3043">
                            <div className="flex items-center justify-end gap-2" data-id="element-3044">
                              <span className={`font-bold ${isGoodMargin ? 'text-emerald-600' : isBadMargin ? 'text-red-600' : 'text-amber-600'}`} data-id="element-3045">
                                {Math.round(item.margin_percent)}%
                              </span>
                              {isGoodMargin ? <TrendingUp className="w-4 h-4 text-emerald-500" data-id="element-3046" /> : isBadMargin ? <TrendingDown className="w-4 h-4 text-red-500" data-id="element-3047" /> : null}
                            </div>
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div> : <div className="text-center py-8 text-gray-500 text-sm" data-id="element-3048">
                No hay datos de rentabilidad disponibles. Asegúrate de
                configurar los costos en el inventario.
              </div>}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>;
}